import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  APP_SLUG_ALIASES,
  canonicalApp,
  directoryIcon,
  findDirectoryApp,
  getAppDirectory,
  loadAppDirectory,
  subscribeAppDirectory,
  type AppDirectory,
  type DirectoryApp,
} from "@flobi/asset-library";
import { gatewayCall } from "../share/gateway";
import apiClient from "../services/apiClient";

/**
 * The App Directory (admin › Product › App Directory, billing-back's public
 * `billing.apps.directory`) as Artwork reads it — owner rules of 2026-10-20: every
 * place an app is drawn by its mark takes the directory's icon, in the person's
 * icon style and the editor's theme (theme.ts `useTheme`). Unknown, loading
 * or failed is an empty box of the same size (ui/AppDirectoryLogo) —
 * never a letter or a stand-in.
 *
 * The kit holds the directory. main.tsx's `configureAppIdentity` reads it with
 * apiClient; Artwork never calls `configureAssetLibrary`, so the kit's
 * `useAppDirectory` would not fetch on its own — `ensureDirectory` asks with
 * apiClient instead. `findDirectoryApp` matches only a row's own names, so
 * `moodboard` / `lumen` go through the kit's alias table first (`dirAppOf`).
 */

let asked = false;

/** Ask for the directory once per page, through apiClient (the kit serves its stored copy meanwhile). */
export function ensureDirectory(): void {
  if (asked) return;
  asked = true;
  loadAppDirectory({ gateway: "", request: apiClient.request.bind(apiClient) }).catch(() => {
    asked = false;
  });
}

/** The directory's row for an app named by any of its names, or null. */
export function dirAppOf(slug: string | null | undefined, dir: AppDirectory | null = getAppDirectory()): DirectoryApp | null {
  if (!slug || !dir || !Array.isArray(dir.apps)) return null;
  const own = findDirectoryApp(slug, dir);
  if (own) return own;
  const c = canonicalApp(slug);
  for (const name of [c, ...(APP_SLUG_ALIASES[c] ?? [])]) {
    const hit = findDirectoryApp(name, dir);
    if (hit) return hit;
  }
  return null;
}

/** The directory for a React surface; re-renders when an edit lands. */
export function useDirectory(): AppDirectory | null {
  const dir = useSyncExternalStore(subscribeAppDirectory, getAppDirectory, getAppDirectory);
  useEffect(() => {
    ensureDirectory();
  }, []);
  return dir;
}

/* ── the person's icon style ─────────────────────────────────────────────────
   Picked in Flobi Settings › Preferences, stored on the platform preferences row
   (`users.appPreferences`, no appSlug). Read once per page; null until it answers,
   so no mark flashes the default set first. A failed read is Home's default, "3d". */
let iconStyle: string | null = null;
let inflight: Promise<string> | null = null;
const styleListeners = new Set<(s: string) => void>();

function readIconStyle(): Promise<string> {
  if (iconStyle) return Promise.resolve(iconStyle);
  if (!inflight) {
    inflight = gatewayCall<{ iconStyle?: unknown } | null>("users", "users.appPreferences.get", {}, { method: "get" })
      .then((prefs) => (typeof prefs?.iconStyle === "string" && prefs.iconStyle ? prefs.iconStyle : "3d"))
      .catch(() => "3d")
      .then((s) => {
        iconStyle = s;
        inflight = null;
        styleListeners.forEach((fn) => fn(s));
        return s;
      });
  }
  return inflight;
}

/** The person's icon style; null until it is read. */
export function useIconStyle(): string | null {
  const [style, setStyle] = useState<string | null>(iconStyle);
  useEffect(() => {
    styleListeners.add(setStyle);
    void readIconStyle();
    return () => {
      styleListeners.delete(setStyle);
    };
  }, []);
  return style;
}

/**
 * `(slug) => url | null` — an app's directory logo in the person's style on `theme`. null is the
 * empty box: the style or directory is not read yet, or the app has no logo there.
 */
export function useAppLogo(theme: "light" | "dark"): (slug: string) => string | null {
  const dir = useDirectory();
  const style = useIconStyle();
  return useCallback(
    (slug: string) => {
      if (!style) return null;
      const app = dirAppOf(slug, dir);
      return app ? directoryIcon(app, { iconStyle: style, theme }, dir) || null : null;
    },
    [dir, style, theme],
  );
}
